// Copyright (C) 2021-2026 Skylark Drones

import toGeoJson from '@mapbox/togeojson'
import JSZip from 'jszip'

const DTD_DECLARATIONS = ['<!DOCTYPE', '<!ENTITY']
const DTD_DECLARATION_TERMINATORS = [' ', '\t', '\r', '\n', '>']
const UNSAFE_XML_CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/

function containsDTDDeclaration(xml) {
  let index = 0

  while (index < xml.length) {
    if (xml.startsWith('<!--', index)) {
      const commentEnd = xml.indexOf('-->', index + 4)
      index = commentEnd === -1 ? xml.length : commentEnd + 3
      continue
    }

    if (xml.startsWith('<![CDATA[', index)) {
      const cdataEnd = xml.indexOf(']]>', index + 9)
      index = cdataEnd === -1 ? xml.length : cdataEnd + 3
      continue
    }

    const hasDTDDeclaration = DTD_DECLARATIONS.some(declaration => {
      const declarationEnd = index + declaration.length
      return (
        xml.startsWith(declaration, index) &&
        DTD_DECLARATION_TERMINATORS.includes(xml.charAt(declarationEnd))
      )
    })
    if (hasDTDDeclaration) {
      return true
    }

    index += 1
  }

  return false
}

/**
 * Validates XML before it reaches the browser parser. DOMParser has no
 * configuration API for disabling DTDs or external entities, so KML imports
 * reject declarations that can enable either feature before parsing.
 *
 * @param {string} xml XML extracted from a KML or KMZ file
 * @returns {string} normalized XML safe to pass to DOMParser
 */
export function validateAndSanitizeKMLXML(xml) {
  if (typeof xml !== 'string') {
    throw new Error('The KML file could not be read as XML.')
  }

  const sanitizedXML = xml.replace(/^\uFEFF/, '').trim()

  if (!sanitizedXML) {
    throw new Error('The KML file is empty.')
  }

  if (containsDTDDeclaration(sanitizedXML)) {
    throw new Error(
      'KML files containing DOCTYPE or entity declarations are not supported.'
    )
  }

  if (UNSAFE_XML_CONTROL_CHARACTERS.test(sanitizedXML)) {
    throw new Error('The KML file contains invalid XML characters.')
  }

  return sanitizedXML
}

/**
 * Parses a validated KML document with the browser's strict XML parser.
 *
 * @param {string} xml XML extracted from a KML or KMZ file
 * @returns {XMLDocument} parsed KML document
 */
export function parseKMLDocument(xml) {
  const sanitizedXML = validateAndSanitizeKMLXML(xml)

  if (typeof DOMParser === 'undefined') {
    throw new Error('XML parsing is not available in this browser.')
  }

  const kml = new DOMParser().parseFromString(sanitizedXML, 'application/xml')
  const containsParserError =
    kml.documentElement?.localName === 'parsererror' ||
    kml.getElementsByTagName('parsererror').length > 0

  if (containsParserError) {
    throw new Error('The KML file contains malformed XML.')
  }

  if (kml.documentElement?.localName !== 'kml') {
    throw new Error('The uploaded XML document is not a KML file.')
  }

  return kml
}

function parseKMLFeatures(xml) {
  return toGeoJson.kml(parseKMLDocument(xml)).features
}

export function readKML(kmlFile, processKMLFeatures, processError) {
  const fr = new FileReader()
  fr.onload = file => {
    try {
      processKMLFeatures(parseKMLFeatures(file.target.result))
    } catch (error) {
      processError?.(error)
    }
  }
  fr.onerror = () =>
    processError?.(new Error('The KML file could not be read.'))
  fr.readAsText(kmlFile)
}

export function readKMZ(kmzFile, processKMLFeatures, processError) {
  return JSZip.loadAsync(kmzFile)
    .then(zip => {
      let kmlFile = null
      zip.forEach((relPath, file) => {
        // Safety check to ensure that the processing is done only for kml files
        if (
          relPath.split('.').pop().toLowerCase() === 'kml' &&
          kmlFile === null
        ) {
          kmlFile = file
        }
      })

      if (kmlFile === null) {
        return null
      }

      return kmlFile.async('string')
    })
    .then(xml => {
      if (xml === null) {
        processKMLFeatures([])
        return
      }

      processKMLFeatures(parseKMLFeatures(xml))
    })
    .catch(error => processError?.(error))
}
